/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-05-26 22:37:07
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { reduxForm, Field, reset, getFormValues } from 'redux-form';
import { Modal, FormLayout, Stack, Link } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import {
  buildInitialValues,
  validateBeforeSave,
  buildMetafield
} from 'src/helpers/metafield-helper';
import { isRequired, hasTag } from 'src/helpers/form-validation-helper';
import { METAFIELD_APP_REVIEWS } from 'src/constants/metafield';

import * as metafieldActions from 'src/stores/actions/metafield-actions';
import { routes } from 'src/routes';
import history from 'src/routes/history';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';

type Props = {
  active: boolean,
  metafield: Object,
  handleChange: () => void,
  isExceededMetafields: boolean,
  updateMetafield: () => void,
  deleteMetafield: () => void
};

type ComposedProps = Props & WithI18nProps;

const isRequiredValue = isRequired();

class MetafieldModal extends Component<ComposedProps> {
  state = {
    isAppReviewMetafield: false,
    isShowMessageAppliesToSearchForUpdate: false,
    isFirstTimeChangeState: true,
    isShowMessageAppliesToSearchForCreate: false
  };

  componentWillReceiveProps(nextProps) {
    const { saving: nextSaving } = nextProps;
    const { saving, metafield } = this.props;

    // Hide modal
    if (nextSaving === false && saving === true) {
      this.handleChange();
    }

    // First time set value for state
    if (this.state.isFirstTimeChangeState && !_.isEmpty(metafield)) {
      this.setState({
        isShowMessageAppliesToSearchForUpdate: metafield?.appliesTo?.search || false,
        isFirstTimeChangeState: false
      });
    }
  }

  render() {
    const {
      isAppReviewMetafield,
      isShowMessageAppliesToSearchForUpdate,
      isShowMessageAppliesToSearchForCreate
    } = this.state;
    const {
      i18n,
      active,
      saving,
      handleSubmit,
      pristine,
      metafield,
      namespace,
      keyField,
      isExceededMetafields
    } = this.props;

    const title = _.isEmpty(metafield)
      ? i18n.translate('AdminHTML.Metafield.Edit.title_Add')
      : i18n.translate('AdminHTML.Metafield.Edit.title_Edit');

    // Check disable applies to search and filter option when exceeded value
    const { appliesTo = {} } = metafield;
    const { search = false, filterOption = false } = appliesTo;
    const disableAppliesTo = isExceededMetafields && !(search || filterOption);
    const isShowMessageAppliesToSearch = _.isEmpty(metafield)
      ? isShowMessageAppliesToSearchForCreate
      : isShowMessageAppliesToSearchForUpdate;

    return (
      <Modal
        open={active}
        onClose={this.handleChange}
        title={title}
        primaryAction={{
          content: i18n.translate('AdminHTML.Common.Action.save'),
          loading: saving,
          onAction: handleSubmit(this.handleSave.bind(this)),
          disabled: pristine || _.isEmpty(namespace) || _.isEmpty(keyField)
        }}
        secondaryActions={[
          {
            content: i18n.translate('AdminHTML.Common.Action.cancel'),
            onAction: this.handleChange
          }
        ]}>
        <Modal.Section>
          <div className="BC-Modal__Metafield">
            <form onSubmit={this.handleDefaultSubmit}>
              <FormLayout>
                <Field
                  name="namespace"
                  component={RenderTextField}
                  label={i18n.translate('AdminHTML.Metafield.Metafield.namespace')}
                  validate={[isRequiredValue, hasTag]}
                  onChange={text => this.onChange('namespace', text)}
                />
                <Field
                  name="key"
                  component={RenderTextField}
                  label={i18n.translate('AdminHTML.Metafield.Metafield.key')}
                  validate={[isRequiredValue, hasTag]}
                  onChange={text => this.onChange('key', text)}
                />
                <span>{i18n.translate('AdminHTML.Metafield.Metafield.appliesTo')}</span>
                <Stack distribution="fill">
                  <Field
                    name="appliesTo.display"
                    component={RenderCheckbox}
                    label={i18n.translate('AdminHTML.Metafield.Metafield.appliesTo_Display')}
                    isDisabled
                  />
                  {!isAppReviewMetafield && (
                    <Field
                      name="appliesTo.search"
                      component={RenderCheckbox}
                      label={i18n.translate('AdminHTML.Metafield.Metafield.appliesTo_Search')}
                      isDisabled={disableAppliesTo}
                      onChange={this.onChangeAppliesToSearch}
                    />
                  )}
                  {!isAppReviewMetafield && (
                    <Field
                      name="appliesTo.filterOption"
                      component={RenderCheckbox}
                      label={i18n.translate('AdminHTML.Metafield.Metafield.appliesTo_FilterOption')}
                      isDisabled={disableAppliesTo}
                    />
                  )}
                  <Field
                    name="appliesTo.sortByOption"
                    component={RenderCheckbox}
                    label={i18n.translate('AdminHTML.Metafield.Metafield.appliesTo_SortByOption')}
                  />
                </Stack>
                {isShowMessageAppliesToSearch && (
                  <div>
                    {i18n.translate('AdminHTML.Metafield.Notice.messageForApplySearch', {
                      t1: (
                        <Link onClick={() => history.push(routes.relevanceSettings.view())}>
                          {i18n.translate('AdminHTML.Metafield.Notice.t1')}
                        </Link>
                      )
                    })}
                  </div>
                )}
              </FormLayout>
            </form>
          </div>
        </Modal.Section>
      </Modal>
    );
  }

  handleChange = () => {
    this.props.resetForm();
    this.props.handleChange();
  };

  handleSave = async data => {
    const { i18n, metafield: initMetafield, updateMetafield, createMetafield } = this.props;

    if (validateBeforeSave(data, i18n)) {
      const metafield = buildMetafield(data);

      // Update existing metafield
      if (!_.isEmpty(initMetafield)) {
        const { namespace, key } = initMetafield;
        await updateMetafield(namespace, key, metafield);
      } else {
        // Create new metafield
        await createMetafield(metafield);
      }

      Emitter.emit(EVENT_KEY.RELOAD_CSAT_ADMIN_APP_DISPLAY);
    }
  };

  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }

  onChange = (type, text) => {
    const { namespace, keyField, metafield = {} } = this.props;
    const { namespace: currentNamespace, key: currentKey } = metafield;

    const currentReviewMetafield = _.find(METAFIELD_APP_REVIEWS, {
      namespace: currentNamespace,
      key: currentKey
    });
    const isNewMetafield =
      (type === 'namespace' && currentNamespace !== text) ||
      (type === 'key' && currentKey !== text);

    // Only apply to create new metafield or edit non app review metafield
    // => Hide check box: Search and Filter option
    if (
      (!currentReviewMetafield || isNewMetafield) &&
      ((type === 'namespace' &&
        _.find(METAFIELD_APP_REVIEWS, { namespace: text, key: keyField })) ||
        (type === 'key' && _.find(METAFIELD_APP_REVIEWS, { namespace, key: text })))
    ) {
      this.setState({ isAppReviewMetafield: true });
    } else {
      this.setState({ isAppReviewMetafield: false });
    }
  };

  onChangeAppliesToSearch = value => {
    const { metafield } = this.props;

    if (_.isEmpty(metafield)) {
      this.setState({ isShowMessageAppliesToSearchForCreate: value });
    } else {
      this.setState({ isShowMessageAppliesToSearchForUpdate: value });
    }
  };
}

const MetafieldForm = reduxForm({
  form: 'MefafieldForm', // a unique identifier for this form
  enableReinitialize: true
})(MetafieldModal);

const mapStateToProps = (state, props) => {
  const { metafield } = props;
  const formValues = getFormValues('MefafieldForm')(state);

  const saving = _.get(state.metafield, 'saving', false);
  const success = _.get(state.metafield, 'success', false);
  return {
    initialValues: buildInitialValues(metafield),
    namespace: _.get(formValues, 'namespace', ''),
    keyField: _.get(formValues, 'key', ''),
    saving,
    success
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateMetafield: (namespace, key, metafield) =>
    dispatch(metafieldActions.updateMetafield(namespace, key, metafield, ownProps.i18n)),
  createMetafield: metafield =>
    dispatch(metafieldActions.createMetafield(metafield, ownProps.i18n)),
  // Reset form
  resetForm: () => dispatch(reset('MefafieldForm'))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(MetafieldForm));
